import { useEffect, useState } from "react";
import ProfileService from "../../services/Profile";
import { useNavigate } from "react-router-dom";
import {
    FaArrowDown,
    FaArrowUp,
    FaComment,
    FaHeart,
    FaArrowRight,
    FaSignal,
} from "react-icons/fa6";

const GRADIENTS = [
  "from-orange-400 to-red-500",
  "from-blue-400 to-indigo-500",
  "from-green-400 to-emerald-500",
  "from-purple-400 to-pink-500",
  "from-yellow-400 to-orange-500",
  "from-cyan-400 to-blue-500",
];

const MyLatestSignals = () => {
    const navigate = useNavigate();
    const [signals, setSignals] = useState([]);
    const [loading, setLoading] = useState(true);
    const sortedSignals = [...signals].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );

    useEffect(() => {
        const fetchSignals = async () => {
            try {
                const response = await ProfileService.getMySubscribedSignals();

                console.log("My Subscribed Signals:", response);

                setSignals(response?.data?.data || []);
            } catch (error) {
                console.error("Error fetching signals:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchSignals();
    }, []);

    return (
        <section className="min-h-screen px-4 sm:px-6 py-8 bg-gradient-to-br from-white/50 to-purple-50/50">

            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 text-white bg-gradient-to-r from-green-400 to-blue-500 rounded-xl flex items-center justify-center">
                    <FaSignal />
                </div>

                <div>
                    <h2 className="text-xl font-bold text-dark">
                        My Latest Signals
                    </h2>
                    <p className="text-xs text-gray-500">
                        Signals from your subscribed packages
                    </p>
                </div>
            </div>

            {/* Signals */}
            {loading ? (
                <div className="text-center py-10 text-gray-500">
                    Loading signals...
                </div>
            ) : sortedSignals.length === 0 ? (
                <div className="bg-white rounded-2xl p-10 text-center text-gray-500 border border-gray-100">
                    No signals found
                </div>
            ) : (
                <div className="space-y-5">

                    {sortedSignals.map((signal, index) => {
                        const isBuy = signal.type === "buy";

                        const entry =
                            signal.limit > 0
                                ? signal.limit
                                : signal.trigger?.target ?? 0;

                        const hitTps = (signal.tp || []).filter((tp) => tp.done);
                        const lastHit = hitTps[hitTps.length - 1];

                        let outcome = "PENDING";
                        let targetHit = null;

                        if (lastHit) {
                            outcome = "HIT";
                            targetHit = lastHit.target;
                        } else if (signal.sl?.done) {
                            outcome = "STOPPED";
                            targetHit = signal.sl.target;
                        } else if (signal.status === "completed") {
                            outcome = "EXPIRED";
                        }

                        const pct =
                            targetHit != null && entry > 0
                                ? ((isBuy ? targetHit - entry : entry - targetHit) / entry) * 100
                                : null;

                        const profitState =
                            pct === null ? "none" : pct > 0 ? "profit" : pct < 0 ? "loss" : "even";

                        const profitStyles = {
                            profit: { box: "bg-green-50", text: "text-green-600", label: "PROFIT" },
                            loss: { box: "bg-red-50", text: "text-red-600", label: "LOSS" },
                            even: { box: "bg-gray-50", text: "text-gray-500", label: "RETURN" },
                            none: { box: "bg-gray-50", text: "text-gray-500", label: "" },
                        };

                        const ps = profitStyles[profitState];

                        const formatNumber = (value) => {
                            if (value == null || value === "—") return "—";
                            return Number(value).toLocaleString("en-US", {
                                maximumFractionDigits: 2,
                            });
                        };

                        const imageBase = process.env.REACT_APP_BASE_URL_IMAGE;

                        return (
                            <div
                                key={signal._id}
                                onClick={() => navigate(`/signal/${signal._id}`)}
                                className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6 hover:shadow-xl transition-all duration-300 cursor-pointer"
                            >

                                {/* Card Header */}
                                <div className="flex items-start justify-between gap-3 mb-5">

                                    <div className="flex items-center gap-3 min-w-0">

                                        {signal.image?.path ? (
                                            <img
                                                src={imageBase + signal.image.path}
                                                alt={signal.symbol?.symbol}
                                                className="w-14 h-14 rounded-2xl object-cover shrink-0"
                                            />
                                        ) : (
                                            <div className={`w-14 h-14 shrink-0 bg-gradient-to-br ${GRADIENTS[index % GRADIENTS.length]} rounded-2xl flex items-center justify-center`}>
                                                <span className="text-white font-bold text-lg">
                                                    {signal.symbol?.symbol?.slice(0, 4) || "??"}
                                                </span>
                                            </div>
                                        )}

                                        <div className="min-w-0">
                                            <h3 className="font-bold text-lg text-dark truncate">
                                                {signal.symbol?.name || signal.symbol?.symbol || "Unknown"}
                                            </h3>

                                            <p className="text-gray-500 text-sm truncate">
                                                {signal.package?.name || "Package"}
                                            </p>

                                            <p className="text-gray-400 text-xs mt-1">
                                                {new Date(signal.createdAt).toLocaleString()}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex flex-col items-end gap-2 shrink-0">

                                        <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold ${isBuy
                                            ? "bg-green-100 text-green-800"
                                            : "bg-red-100 text-red-800"
                                            }`}>
                                            {isBuy ? <FaArrowUp /> : <FaArrowDown />}
                                            {isBuy ? "BUY" : "SELL"}
                                        </span>

                                        <div className={`${ps.box} px-3 py-2 rounded-xl text-right min-w-[90px]`}>
                                            <p className={`text-xl font-bold ${ps.text}`}>
                                                {pct === null ? "—" : `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`}
                                            </p>
                                            <p className={`${ps.text} text-xs font-semibold`}>
                                                {ps.label}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Price Information */}
                                <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4">

                                    <div className="bg-gray-50 rounded-xl p-3 text-center min-w-0">
                                        <p className="text-gray-500 text-xs mb-2">Entry Price</p>
                                        <p className="font-bold text-sm text-dark break-words">
                                            {formatNumber(entry)}
                                        </p>
                                    </div>

                                    <div className="bg-gray-50 rounded-xl p-3 text-center min-w-0">
                                        <p className="text-gray-500 text-xs mb-2">Target Hit</p>
                                        <p className="font-bold text-sm text-green-600 break-words">
                                            {formatNumber(targetHit)}
                                        </p>
                                    </div>

                                    <div className="bg-gray-50 rounded-xl p-3 text-center min-w-0">
                                        <p className="text-gray-500 text-xs mb-2">Status</p>
                                        <p className="font-bold text-xs sm:text-sm text-dark break-words">
                                            {outcome}
                                        </p>
                                    </div>

                                </div>

                                {/* Footer */}
                                <div className="flex items-center justify-between pt-3 border-t border-gray-100">

                                    <div className="flex items-center gap-4 text-gray-500 text-sm">

                                        <span className="flex items-center gap-1">
                                            <FaHeart className="text-red-400" />
                                            {signal.likes?.length ?? 0}
                                        </span>

                                        <span className="flex items-center gap-1">
                                            <FaComment className="text-blue-400" />
                                            {signal.commentsCount ?? 0}
                                        </span>

                                    </div>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/signal/${signal._id}`);
                                        }}
                                        className="bg-gradient-to-r from-blue-500 to-blue-600 text-white w-10 h-10 rounded-xl hover:shadow-lg transition-all flex items-center justify-center"
                                    >
                                        <FaArrowRight />
                                    </button>

                                </div>

                            </div>
                        );
                    })}

                </div>
            )}
        </section>
    );
};

export default MyLatestSignals;